import {observable, flow, makeObservable} from "mobx";

import { EAIClient } from "Services/EAIClient";
import { appStore } from "../";
import {Student, TableParams} from "Stores/commonStore";

export interface StudentGroup {
    ruuid: string
    school_id: string
    name: string
    description: string
    is_active: boolean
    admins_can_view: boolean
    faculty_can_view: boolean
    mentors_can_view: boolean
    public_can_view: boolean
    view_committee_id: number | null
    group_type: string
}

export interface StudentDataSource {
    id: string
    school_id: string
    enflux_student_ruuid: string
    datasource: string
    source_id: string
}

interface DefaultResult {
    totalCount: number
}

export interface StudentGroupDataQuery {
    data: StudentGroup[]
    result: DefaultResult
}


class StudentStore {
    @observable isProcessing = false;
    @observable isFetchingStudentsForGroup = false;
    @observable isFetchingDataSource = false;
    @observable studentGroups: StudentGroup[] = [];
    @observable totalStudentGroups: number;

    client = new EAIClient();
    lastStudentFetch: number;

    constructor() {
        makeObservable(this);
    }

    @flow
    *addGroupsToStudent(studentRuuid: string, groups: string[]): any {
        this.isProcessing = true;
        try {
            yield this.client.addGroupsToStudent(studentRuuid, groups);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *fetchStudentGroups(params?: TableParams): any {
        this.isProcessing = true;
        try {
            const studentGroupsResp = yield this.client.fetchStudentGroups(params);
            this.studentGroups = studentGroupsResp.data.data;
            this.totalStudentGroups = studentGroupsResp.data.result.total_count;

        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    async fetchStudentGroupsAsync (params?: TableParams) {
        this.lastStudentFetch = Date.now();
        try {
            const resp = await this.client.fetchStudentGroups(params, this.lastStudentFetch);
            if (resp.data.result.timestamp < this.lastStudentFetch) return;
            this.totalStudentGroups = resp.data.result.total_count;
            this.studentGroups = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    * fetchGroupsForStudent(studentRuuid: string): any {
        this.isProcessing = true;
        try {
            const studentGroupsResp = yield this.client.fetchGroupsForStudent(studentRuuid);
            return studentGroupsResp.data.data as StudentGroup[];
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * fetchStudentsForGroup(groupId: string): any {
        this.isFetchingStudentsForGroup = true;
        try {
            const studentsResp = yield this.client.fetchStudentsForGroup(groupId);
            return studentsResp.data.data.map(student => student as Student);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingStudentsForGroup = false;
        }
    }

    @flow
    *createStudentGroup(data: Partial<StudentGroup>, students: Student[], users: string[]): any {
        this.isProcessing = true;
        try{
            yield this.client.createStudentGroup(data, students, users);
        } catch(e) {
            throw e;
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *updateStudentGroup(groupId: string, data: Partial<StudentGroup>, students: Student[], users: string[]): any {
        this.isProcessing = true;
        try{
            yield this.client.updateStudentGroup(groupId, data, students, users);
        } catch(e){
            throw e;
        } finally {
            this.isProcessing = false;
        }
    }

    disabledStudent = flow(function *(this: StudentStore, ruuid: string, disabled: boolean) {
        try {
            yield this.client.overridesStudent(ruuid, disabled);
        } catch (e) {
            appStore.addError(e);
        }
    });

    @flow
    *deleteStudentGroup(groupId: string): any {
        this.isProcessing = true;
        try {
            yield this.client.deleteStudentGroup(groupId);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    *fetchStudentDataSource(studentRuuid: string): any {
        this.isFetchingDataSource = true;
        try {
            const dataSourcesResp = yield this.client.fetchStudentDataSource(studentRuuid);
            return dataSourcesResp.data.data as StudentDataSource[];
        } catch (e) {

        } finally {
            this.isFetchingDataSource = false;
        }
    }
}

export { StudentStore };
